/**
 * Build a WebVTT captions track for an exercise's silent demo video, using
 * the exercise's `execution` steps distributed evenly across the video duration.
 *
 * Result is returned as a Blob object-URL suitable for use in a
 * <track src={...}> element. The caller is responsible for revoking the URL
 * when the video is closed to avoid memory leaks.
 *
 * WCAG 1.2.2 (Captions - Prerecorded, Level A) compliance: since the demo
 * videos have no audio dialogue, these captions describe the visible action
 * ("Baja la barra al pecho…", "Empuja con fuerza…") synced to the on-screen
 * demonstration. Uses kind="captions" (universal browser support) which the
 * user can toggle from the video's native controls.
 *
 * @param {string[]} steps  Array of execution/technique step strings.
 * @param {number} durationSeconds  Total video duration (from loadedmetadata).
 * @returns {string} object-URL for the .vtt Blob.
 */
export function buildExerciseVTT(steps, durationSeconds) {
  if (!Array.isArray(steps) || steps.length === 0 || !Number.isFinite(durationSeconds) || durationSeconds <= 0) {
    return null;
  }

  const slice = durationSeconds / steps.length;
  const cues = steps.map((text, i) => {
    const start = i * slice;
    const end = (i + 1) * slice;
    return `${i + 1}\n${fmt(start)} --> ${fmt(end)}\n${escape(text)}\n`;
  });

  const vtt = `WEBVTT\n\n${cues.join("\n")}`;
  const blob = new Blob([vtt], { type: "text/vtt" });
  return URL.createObjectURL(blob);
}

function fmt(seconds) {
  const s = Math.max(0, seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const secs = s % 60;
  const secWhole = Math.floor(secs);
  const ms = Math.round((secs - secWhole) * 1000);
  const pad = (n, w = 2) => String(n).padStart(w, "0");
  return `${pad(h)}:${pad(m)}:${pad(secWhole)}.${pad(ms, 3)}`;
}

function escape(t) {
  // Basic escaping for VTT — remove markup-like chars, keep punctuation.
  return String(t).replace(/[<>&]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" }[c]));
}
